import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, Subscription } from 'rxjs';
import { RawMaterialService } from '../../raw-material.service';
import {
  StockAdjustment,
  StockAdjustmentContext,
  StockAdjustmentDirection,
  StockAdjustmentReason,
  StockAdjustmentRequest,
  StockShortageError,
  STOCK_ADJUSTMENT_REASON_OPTIONS,
} from '../../raw-material.model';
import { SnackbarService } from '../../../services/snackbar.service';
import {
  absDecimalString,
  decimalSign,
  formatDecimalQuantity,
  SnackbarAlertStatus,
  toNumberOrNull,
} from '../../../utils';

/** What a "Reverse this adjustment" action hands the form to start from. */
export interface StockAdjustmentPrefill {
  direction: StockAdjustmentDirection;
  quantity: string;
  reason?: StockAdjustmentReason;
  remarks?: string;
}

/** Positive numbers only. The sign comes from the Add/Remove toggle, never from typing. */
function positiveQuantityValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') return null;

  const quantity = toNumberOrNull(value);
  if (quantity === null) return { quantityInvalid: true };
  if (quantity <= 0) return { quantityNotPositive: true };

  return null;
}

@Component({
  selector: 'app-stock-adjustment-form',
  standalone: false,
  templateUrl: './stock-adjustment-form.component.html',
  styleUrl: './stock-adjustment-form.component.scss',
})
export class StockAdjustmentFormComponent implements OnInit, OnDestroy {
  /** The material and warehouse being adjusted. Both are fixed: the form never picks them. */
  @Input({ required: true }) context!: StockAdjustmentContext;
  @Input() prefill: StockAdjustmentPrefill | null = null;

  @Output() saved: EventEmitter<StockAdjustment> = new EventEmitter<StockAdjustment>();
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();

  protected adjustmentForm!: FormGroup;
  protected isSubmitting: boolean = false;

  /** Server said the removal exceeds stock. Its numbers are the whole point: show them. */
  protected shortageError: StockShortageError | null = null;
  protected quantityError: string | null = null;
  protected remarksError: string | null = null;
  protected submitError: any = null;
  protected isForbidden: boolean = false;

  protected readonly reasonOptions = STOCK_ADJUSTMENT_REASON_OPTIONS;
  protected readonly Direction = StockAdjustmentDirection;

  private submitSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private rawMaterialService: RawMaterialService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.adjustmentForm = this.fb.group({
      direction: [this.prefill?.direction ?? StockAdjustmentDirection.ADD, Validators.required],
      quantity: [this.prefill?.quantity ?? null, [Validators.required, positiveQuantityValidator]],
      reason: [this.prefill?.reason ?? null, Validators.required],
      remarks: [this.prefill?.remarks ?? ''],
    });

    // The server requires remarks only for "other", so mirror that rather than
    // demanding an explanation for every adjustment.
    this.reasonControl.valueChanges.subscribe(() => this.applyRemarksRequirement());
    this.applyRemarksRequirement();
  }

  protected get directionControl(): AbstractControl {
    return this.adjustmentForm.get('direction')!;
  }

  protected get quantityControl(): AbstractControl {
    return this.adjustmentForm.get('quantity')!;
  }

  protected get reasonControl(): AbstractControl {
    return this.adjustmentForm.get('reason')!;
  }

  protected get remarksControl(): AbstractControl {
    return this.adjustmentForm.get('remarks')!;
  }

  protected get isRemoving(): boolean {
    return this.directionControl.value === StockAdjustmentDirection.REMOVE;
  }

  protected get isRemarksRequired(): boolean {
    return this.reasonControl.value === StockAdjustmentReason.OTHER;
  }

  protected get availableLabel(): string | null {
    const available = this.context?.units_available;
    if (available === null || available === undefined || String(available).trim() === '') return null;

    return `${formatDecimalQuantity(available)} ${this.context.unit_short_name}`;
  }

  /** Restates the pending change in words, so a mis-set toggle is visible before saving. */
  protected get previewLabel(): string | null {
    const quantity = toNumberOrNull(this.quantityControl.value);
    if (quantity === null || quantity <= 0) return null;

    const verb = this.isRemoving ? 'Removing' : 'Adding';
    const preposition = this.isRemoving ? 'from' : 'to';
    const amount = formatDecimalQuantity(String(quantity));

    return `${verb} ${amount} ${this.context.unit_short_name} ${preposition} ${this.context.raw_material_name} at ${this.context.warehouse_name}.`;
  }

  protected setDirection(direction: StockAdjustmentDirection): void {
    this.directionControl.setValue(direction);
    // A shortage was about the previous direction and quantity; it no longer applies.
    this.shortageError = null;
    this.quantityError = null;
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }

  protected onSubmit(): void {
    this.adjustmentForm.markAllAsTouched();
    if (this.adjustmentForm.invalid || this.isSubmitting) return;

    this.clearServerErrors();
    this.isSubmitting = true;

    this.submitSubscription = this.rawMaterialService
      .create_stock_adjustment(this.buildRequest())
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (adjustment: StockAdjustment) => {
          this.snackbarService.show(this.buildSuccessMessage(adjustment), SnackbarAlertStatus.SUCCESS, 6000);
          this.saved.emit(adjustment);
        },
        error: (error: HttpErrorResponse) => this.handleSubmitError(error),
      });
  }

  /** The toggle supplies the sign; the input only ever holds a positive number. */
  private buildRequest(): StockAdjustmentRequest {
    const { quantity, reason, remarks } = this.adjustmentForm.value;
    const magnitude = String(quantity).trim().replace(/^\+/, '');

    const reqBody: StockAdjustmentRequest = {
      raw_material: this.context.raw_material,
      warehouse: this.context.warehouse,
      quantity: this.isRemoving ? `-${magnitude}` : magnitude,
      reason,
    };

    const trimmedRemarks = (remarks ?? '').trim();
    if (trimmedRemarks) {
      reqBody.remarks = trimmedRemarks;
    }

    return reqBody;
  }

  /** Balance comes from the response, never from arithmetic on this side. */
  private buildSuccessMessage(adjustment: StockAdjustment): string {
    const verb = decimalSign(adjustment.quantity) < 0 ? 'Removed' : 'Added';
    const amount = formatDecimalQuantity(absDecimalString(adjustment.quantity));
    const balance = formatDecimalQuantity(adjustment.units_available_after);
    const unit = adjustment.unit_short_name;

    return (
      `${verb} ${amount} ${unit}. ` +
      `${adjustment.raw_material_name} at ${adjustment.warehouse_name} is now ${balance} ${unit}.`
    );
  }

  private handleSubmitError(error: HttpErrorResponse): void {
    this.clearServerErrors();

    if (error?.status === 403) {
      this.isForbidden = true;
      return;
    }

    const body = error?.error;
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
      this.submitError = error;
      return;
    }

    if (isStockShortageError(body)) {
      this.shortageError = body;
      return;
    }

    // Standard DRF field errors: peel off the two the form can point at, and let the
    // shared alert render whatever is left rather than dropping it.
    const rest: Record<string, unknown> = { ...body };

    if (rest['quantity']) {
      this.quantityError = firstErrorMessage(rest['quantity']);
      delete rest['quantity'];
    }

    if (rest['remarks']) {
      this.remarksError = firstErrorMessage(rest['remarks']);
      delete rest['remarks'];
    }

    if (Object.keys(rest).length > 0) {
      this.submitError = { error: rest };
    }
  }

  private applyRemarksRequirement(): void {
    if (this.isRemarksRequired) {
      this.remarksControl.addValidators(Validators.required);
    } else {
      this.remarksControl.removeValidators(Validators.required);
    }

    this.remarksControl.updateValueAndValidity({ emitEvent: false });
  }

  private clearServerErrors(): void {
    this.shortageError = null;
    this.quantityError = null;
    this.remarksError = null;
    this.submitError = null;
    this.isForbidden = false;
  }

  protected formatQuantity(value: string | null | undefined): string {
    return formatDecimalQuantity(value);
  }

  ngOnDestroy(): void {
    this.submitSubscription?.unsubscribe();
  }
}

/** The shortage response is the only 400 that carries these three figures together. */
function isStockShortageError(body: any): body is StockShortageError {
  return (
    typeof body?.quantity === 'string' &&
    body?.available !== undefined &&
    body?.requested !== undefined &&
    body?.shortage !== undefined
  );
}

function firstErrorMessage(value: unknown): string {
  if (Array.isArray(value)) return String(value[0] ?? '');

  return String(value ?? '');
}
